/**
 * Utility functions for volume delta calculations and data processing
 */

/**
 * Filter sessions by number of days
 * @param {Array} sessions - All sessions
 * @param {number|string} days - Number of days (7, 30, 'all', or 'trend')
 * @returns {Array} Filtered sessions
 */
export const filterSessionsByDays = (sessions, days) => {
  if (!sessions || sessions.length === 0) return [];
  
  // Return all sessions if 'all' is specified
  if (days === 'all') return sessions;
  
  const now = new Date();
  const daysNum = days === 'trend' ? 30 : parseInt(days);
  const cutoff = new Date(now.getTime() - daysNum * 24 * 60 * 60 * 1000);
  
  return sessions.filter(s => {
    const sessionDate = new Date(s.created_at);
    return sessionDate >= cutoff;
  });
};

/**
 * Filter sessions by exercise type
 * @param {Array} sessions - All sessions
 * @param {string} exerciseType - 'all', 'scale', or 'arpeggio'
 * @returns {Array} Filtered sessions
 */
export const filterSessionsByType = (sessions, exerciseType) => {
  if (!sessions || sessions.length === 0) return [];
  if (exerciseType === 'all') return sessions;
  
  return sessions.filter(s => s.exercise_type === exerciseType);
};

/**
 * Filter sessions by direction
 * @param {Array} sessions - All sessions
 * @param {string} direction - 'all', 'up', or 'down'
 * @returns {Array} Filtered sessions (note: this affects which fields are used for analysis)
 */
export const filterSessionsByDirection = (sessions, direction) => {
  if (!sessions || sessions.length === 0) return [];
  if (direction === 'all') return sessions;
  
  // For now, we don't filter out sessions - instead this will be used by analysis components
  // to select the appropriate _up or _down fields when displaying data
  // Return all sessions but with a direction marker
  return sessions.map(s => ({ ...s, _directionFilter: direction }));
};

/**
 * Filter sessions by articulation type
 * @param {Array} sessions - All sessions
 * @param {string} articulationType - 'all', 'staccato', or 'legato'
 * @returns {Array} Filtered sessions
 */
export const filterSessionsByArticulation = (sessions, articulationType) => {
  if (!sessions || sessions.length === 0) return [];
  if (articulationType === 'all') return sessions;
  
  return sessions.filter(s => s.articulation_type === articulationType);
};

/**
 * Get the last N sessions
 * @param {Array} sessions - All sessions (sorted by date desc)
 * @param {number} n - Number of sessions to get
 * @returns {Array} Last N sessions
 */
export const getLastNSessions = (sessions, n) => {
  if (!sessions || sessions.length === 0) return [];
  return sessions.slice(0, n);
};

/**
 * Calculate average of a field across sessions
 * @param {Array} sessions - Sessions to average
 * @param {string} field - Field name to average
 * @returns {number} Average value
 */
export const calculateAverage = (sessions, field) => {
  if (!sessions || sessions.length === 0) return null;
  
  const validSessions = sessions.filter(s => s[field] != null);
  if (validSessions.length === 0) return null;
  
  const sum = validSessions.reduce((acc, s) => acc + s[field], 0);
  return sum / validSessions.length;
};

/**
 * Calculate median of a field across sessions
 * @param {Array} sessions - Sessions
 * @param {string} field - Field name
 * @returns {number} Median value
 */
export const calculateMedian = (sessions, field) => {
  if (!sessions || sessions.length === 0) return null;
  
  const values = sessions
    .filter(s => s[field] != null)
    .map(s => s[field])
    .sort((a, b) => a - b);
  
  if (values.length === 0) return null;
  
  const mid = Math.floor(values.length / 2);
  return values.length % 2 === 0
    ? (values[mid - 1] + values[mid]) / 2
    : values[mid];
};

/**
 * Calculate trend (last 5 avg vs last 30 avg)
 * @param {Array} sessions - All sessions
 * @param {string} field - Field name to calculate trend for
 * @returns {Object} {last5, last30, difference}
 */
export const calculateTrend = (sessions, field) => {
  if (!sessions || sessions.length === 0) {
    return { last5: null, last30: null, difference: null };
  }
  
  const last5 = calculateAverage(getLastNSessions(sessions, 5), field);
  const last30 = calculateAverage(getLastNSessions(sessions, 30), field);
  
  return {
    last5,
    last30,
    difference: last5 != null && last30 != null ? last5 - last30 : null
  };
};

/**
 * Get loudest and quietest finger for a hand
 * @param {Array} sessions - Sessions to analyze
 * @param {string} hand - 'left' or 'right'
 * @returns {Object} {loudest: {finger, avg}, quietest: {finger, avg}}
 */
export const getLoudestQuietestFingers = (sessions, hand) => {
  if (!sessions || sessions.length === 0) {
    return { loudest: null, quietest: null };
  }
  
  const fingerAverages = [];
  
  for (let i = 1; i <= 5; i++) {
    const field = `${hand}_finger_${i}_relative_volume_avg`;
    const avg = calculateAverage(sessions, field);
    if (avg !== null) {
      fingerAverages.push({ finger: i, avg });
    }
  }
  
  if (fingerAverages.length === 0) {
    return { loudest: null, quietest: null };
  }
  
  // Sort by actual value (not absolute value)
  // Loudest = most positive, Quietest = most negative
  fingerAverages.sort((a, b) => a.avg - b.avg);
  
  const quietest = fingerAverages[0]; // Most negative
  const loudest = fingerAverages[fingerAverages.length - 1]; // Most positive
  
  // If quietest is above 0, there's no truly quiet finger
  // If loudest is below 0, there's no truly loud finger
  return {
    loudest: loudest.avg < 0 ? null : loudest,
    quietest: quietest.avg > 0 ? null : quietest
  };
};

/**
 * Calculate in-range percentage for volume delta
 * @param {Array} sessions - Sessions to analyze
 * @param {string} field - Field name to check
 * @param {number} threshold - Threshold for "in range" (default 2)
 * @returns {number} Percentage (0-100)
 */
export const calculateInRangePercentage = (sessions, field, threshold = 2) => {
  if (!sessions || sessions.length === 0) return null;
  
  const validSessions = sessions.filter(s => s[field] != null);
  if (validSessions.length === 0) return null;
  
  const inRange = validSessions.filter(s => Math.abs(s[field]) <= threshold);
  return (inRange.length / validSessions.length) * 100;
};

/**
 * Get all unique exercises of a specific type from sessions
 * @param {Array} sessions - All sessions
 * @param {string} exerciseType - Exercise type
 * @returns {Array} Unique exercise names
 */
export const getExercisesByType = (sessions, exerciseType) => {
  if (!sessions || sessions.length === 0) return [];
  
  const exercisesOfType = sessions.filter(s => s.exercise_type === exerciseType);
  const uniqueExercises = [...new Set(exercisesOfType.map(s => s.exercise_name))];
  return uniqueExercises.sort();
};

/**
 * Calculate heatmap value for a specific cell
 * @param {Array} sessions - Filtered sessions
 * @param {string} entity - 'left', 'right', 'left_finger_1', etc.
 * @param {string} exercise - Exercise name or type
 * @param {string} timeMode - '1', '7', '30', or 'trend'
 * @returns {number|Object} Value or trend object
 */
export const calculateHeatmapValue = (sessions, entity, exercise, timeMode) => {
  // Filter sessions for this exercise
  let filteredSessions = sessions;
  if (exercise && exercise !== 'all') {
    filteredSessions = sessions.filter(s => s.exercise_name === exercise || s.exercise_type === exercise);
  }
  
  // Determine field name
  const field = `${entity}_relative_volume_avg`;
  
  if (timeMode === 'trend') {
    return calculateTrend(filteredSessions, field);
  } else {
    return calculateAverage(filteredSessions, field);
  }
};

/**
 * Calculate opacity for heatmap cell based on value
 * @param {number} value - Volume delta value
 * @returns {number} Opacity (0-1)
 */
export const calculateHeatmapOpacity = (value) => {
  if (value == null) return 0;
  const absValue = Math.abs(value);
  return Math.min(absValue / 15, 1);
};

/**
 * Calculate bidirectional color for heatmap cell
 * Orange for too loud (positive), purple for too quiet (negative), white for perfect (0)
 * @param {number} value - Volume delta value
 * @returns {string} RGB color string
 */
export const calculateHeatmapColor = (value) => {
  if (value == null) return 'rgb(255, 255, 255)'; // white for no data
  
  const absValue = Math.abs(value);
  const intensity = Math.min(absValue / 15, 1); // normalize to 0-1
  
  if (value > 0) {
    // Too loud - orange (more intense orange as value increases)
    // Orange color: rgb(234, 88, 12) or #ea580c
    const r = 255;
    const g = Math.round(255 - (255 - 88) * intensity);
    const b = Math.round(255 - (255 - 12) * intensity);
    return `rgb(${r}, ${g}, ${b})`;
  } else if (value < 0) {
    // Too quiet - purple (more intense purple as value decreases)
    // Purple color: rgb(147, 51, 234) or #9333ea
    const r = Math.round(255 - (255 - 147) * intensity);
    const g = Math.round(255 - (255 - 51) * intensity);
    const b = 255;
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    // Perfect - white
    return 'rgb(255, 255, 255)';
  }
};

/**
 * Linear regression calculation
 * @param {Array} points - Array of {x, y} points
 * @returns {Object} {slope, intercept}
 */
export const calculateLinearRegression = (points) => {
  if (!points || points.length < 2) return null;
  
  const n = points.length;
  const sumX = points.reduce((acc, p) => acc + p.x, 0);
  const sumY = points.reduce((acc, p) => acc + p.y, 0);
  const sumXY = points.reduce((acc, p) => acc + p.x * p.y, 0);
  const sumX2 = points.reduce((acc, p) => acc + p.x * p.x, 0);
  
  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  return { slope, intercept };
};

/**
 * Prepare data for trend over time scatter plot
 * @param {Array} sessions - All sessions (30 days)
 * @param {string} hand - 'left' or 'right'
 * @param {Array} fingers - Array of finger numbers to plot
 * @returns {Object} Data organized by finger
 */
export const prepareTrendOverTimeData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_relative_volume_avg`;
    const points = sessions
      .filter(s => s[field] != null)
      .map(s => ({
        x: new Date(s.created_at).getTime(),
        y: s[field],
        date: s.created_at
      }));
    
    const regression = calculateLinearRegression(points);
    
    data[finger] = { points, regression };
  });
  
  return data;
};

/**
 * Prepare data for trend over tempo scatter plot
 * @param {Array} sessions - All sessions (30 days)
 * @param {string} hand - 'left' or 'right'
 * @param {Array} fingers - Array of finger numbers to plot
 * @returns {Object} Data organized by finger
 */
export const prepareTrendOverTempoData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_relative_volume_avg`;
    const points = sessions
      .filter(s => s[field] != null && s.calculated_tempo != null)
      .map(s => ({
        x: s.calculated_tempo,
        y: s[field]
      }));
    
    data[finger] = { points };
  });
  
  return data;
};

/**
 * Format balance value for display
 * @param {number} balance - Balance value (positive = RH louder)
 * @param {string} mode - 'simple' or 'trend'
 * @param {Object} trendData - Trend data if mode is 'trend'
 * @returns {string} Formatted string
 */
export const formatBalance = (balance, mode = 'simple', trendData = null) => {
  if (balance == null) return 'N/A';
  
  const absBalance = Math.abs(balance);
  const hand = balance > 0 ? 'RH' : 'LH';
  
  if (mode === 'trend' && trendData) {
    const { last5, last30, difference } = trendData;
    return `Last 5: ${last5?.toFixed(1)} | Last 30: ${last30?.toFixed(1)} | Δ: ${difference?.toFixed(1)}`;
  }
  
  return `${hand} louder by ${absBalance.toFixed(1)}`;
};

/**
 * Prepare data for trend over repetition scatter plot
 * @param {Array} sessions - All sessions (30 days)
 * @param {string} hand - 'left' or 'right'
 * @param {Array} fingers - Array of finger numbers to plot
 * @returns {Object} Data organized by finger
 */
export const prepareTrendOverRepetitionData = (sessions, hand, fingers) => {
  if (!sessions || sessions.length === 0) return {};
  
  const data = {};
  
  fingers.forEach(finger => {
    const field = `${hand}_finger_${finger}_relative_volume_avg`;
    const points = sessions
      .filter(s => s[field] != null)
      .map((s, idx) => ({
        x: sessions.length - idx, // Reverse so 1 is most recent
        y: s[field],
        date: s.created_at
      }))
      .reverse(); // Reverse so repetition 1 is oldest
    
    const regression = calculateLinearRegression(points);
    
    data[finger] = { points, regression };
  });
  
  return data;
};

